import { useContext } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import SimulateService from '../../../../service/SimulateService'
import MissionService from '../../../../service/MissionService'
import { getApiFailureMessage, showToastMessage } from '../../../../utils/commonFunctions'
import { PresimDataType, PresimPaneltype } from '../utils/scenario-types'
import useUserData from './useUserData'
import { SCENARIO_OPERATION_TYPES } from '../../constants'
import { ScenarioResponseType } from '../../types'

const usePresimApis = () => {
    const { userData } = useUserData()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('usePresimApis must be used within a Scenario States Provider')
    }
    const { setFormData, setThermalHeatmapData, setThermalEnvParams, setThermalModel, scenarioBaseData } = context

    const setPresimLoader = (loading: boolean, satelliteId: string) => {
        setFormData((prev) => ({
            ...prev,
            assets: {
                ...prev.assets,
                presim: {
                    ...prev.assets.presim,
                    data: prev.assets.presim.data ? {
                        ...prev.assets.presim.data,
                        [satelliteId]: {
                            ...prev.assets.presim.data[satelliteId],
                            status: loading ? 'running' : 'completed'
                        }
                    } : prev.assets.presim.data,
                },
            },
        }));
    }

    const stopAllPresimLoaders = () => {
        setFormData((prev) => ({
            ...prev,
            assets: {
                ...prev.assets,
                presim: {
                    ...prev.assets.presim,
                    data: prev.assets.presim.data ? {
                        ...prev.assets.presim.data,
                        ...Object.fromEntries(Object.entries(prev.assets.presim.data).map(([key, value]) => [key, { ...value, status: 'idle' }]))
                    } : prev.assets.presim.data
                },
            },
        }));
    }

    const postPresimData = async (missionId: string, scenarioId: string, groupId: string, satelliteId: string, fidelity: string, scheduleId: string | null) => {
        setPresimLoader(true, satelliteId)
        try {
            await SimulateService.postPredictionData(missionId, scenarioId, groupId, satelliteId, fidelity, scheduleId)
        } catch (error) {
            const message = getApiFailureMessage(error, 'Post');
            setPresimLoader(false, satelliteId)
            showToastMessage('error', message)
        }
    }

    const getPresimData = async (
        missionId: string,
        scenarioId: string,
        scenario: ScenarioResponseType,
        previousPresimData: PresimDataType,
        selectedSatellite?: string
    ): Promise<PresimDataType> => {
        let orbitDetails: any = null;
        const scenarioOperation = scenario?.scenarioOperation;
        const allPresimdata: PresimDataType = { ...previousPresimData };

        try {
            const response = await SimulateService.getPredictionData(missionId, scenarioId);
            const urls = response?.data?.urls;

            if (!urls) return allPresimdata;

            const flagDataCompress =
                userData?.["feature-allowed"]?.["dataCompress"] === "Yes";

            const isConops = scenarioOperation === SCENARIO_OPERATION_TYPES.CONOPS;
            const isLeops = scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP;

            // Resolve groups with thermal info — context may not be populated on initial load
            let resolvedGroups = scenarioBaseData.allGroups;
            if (!resolvedGroups.length) {
                try {
                    const groupsRes = await SimulateService.getSatelliteGroups(missionId);
                    resolvedGroups = (groupsRes?.data || []).map((g: any) => ({
                        groupId: g?.uniqueId,
                        thermal: g?.satConfig?.thermal
                    })) as any;
                } catch {
                    // proceed without thermal filtering
                }
            }

            for (const group of urls) {
                const groupId = group?.SatelliteGroupId;
                if (!groupId) continue;

                let thermalConfigured = false;
                try {
                    const { data: thermalConfig } = await MissionService.getThermalConfig(missionId, groupId);
                    thermalConfigured = thermalConfig?.configComplete === true;
                    if (thermalConfigured) {
                        setThermalEnvParams({
                            albedo_factor: thermalConfig.albedo_factor ?? null,
                            blackbody_temperature: thermalConfig.blackbody_temperature ?? null,
                            solar_constant: thermalConfig.solar_constant ?? null,
                        });
                        setThermalModel(thermalConfig.thermalModel ?? null);
                    }
                } catch {
                    thermalConfigured = false;
                }

                for (const sat of group?.SatellitesPanelsUrls || []) {

                    const satelliteId = sat?.SatelliteId;
                    if (!satelliteId) continue;

                    const isSimDone =
                        sat?.PreSimStatus === "DONE" &&
                        (
                            (isConops && sat?.SchPredictionData) ||
                            (isLeops && sat?.LeOpsSimData)
                        );

                    // NOT DONE → Idle or Running
                    if (!isSimDone) {
                        const isRunning = sat?.PreSimStatus === "IN-PROGRESS";

                        allPresimdata[satelliteId] = {
                            status: isRunning ? "running" : "idle",
                            result: {
                                agilityUrl: [],
                                powerUrl: [],
                                dataFlowUrl: [],
                                thermalUrl: [],
                            },
                            orbitDetails: null,
                        };

                        // next satellite
                        continue;
                    }

                    // DONE → Fetch Orbit + Panel data
                    if (!isLeops) {
                        const { data } = await SimulateService.getPredictionOrbitOrientationData(
                            missionId,
                            scenarioId,
                            groupId,
                            satelliteId
                        );
                        orbitDetails = data;
                        // Thermal Heatmap — only call if thermal config is complete for this group
                        if (thermalConfigured) {
                            try {
                                const { data: thermalData } = await SimulateService.getHeatMapReport(
                                    missionId,
                                    scenarioId,
                                    groupId,
                                    satelliteId
                                );
                                setThermalHeatmapData((prev: any) => ({ ...(prev || {}), [groupId]: thermalData }))
                            } catch {
                                showToastMessage('error', 'Failed to load thermal heatmap data')
                            }
                        }
                    }

                    const ds = isConops
                        ? sat?.SchPredictionData
                        : sat?.LeOpsSimData;

                    const agility = ds?.adcs || [];
                    const power = ds?.power || [];
                    const thermal = ds?.thermal || [];
                    const dataFlowRaw = ds?.dataflow || [];

                    const dataFlow = dataFlowRaw.filter((panel: PresimPaneltype) =>
                        flagDataCompress
                            ? panel.panel_name !== "Summary"
                            : panel.panel_name !== "Data Summary"
                    );

                    allPresimdata[satelliteId] = {
                        status:
                            selectedSatellite
                                ? selectedSatellite === satelliteId
                                    ? "completed"
                                    : allPresimdata[satelliteId]?.status || "idle"
                                : "completed",

                        result: {
                            agilityUrl: [...agility].sort((a, b) => a.id - b.id),
                            powerUrl: [...power].sort((a, b) => a.id - b.id),
                            dataFlowUrl: [...dataFlow].sort((a, b) => a.id - b.id),
                            thermalUrl: thermalConfigured ? [...thermal].sort((a, b) => a.id - b.id) : [],
                        },
                        orbitDetails: orbitDetails,
                    };
                }
            }

            return allPresimdata;

        } catch (error) {
            console.error("Presim fetch error:", error);
            return allPresimdata;
        } finally {
            stopAllPresimLoaders();
        }
    };



    const handleExportTelemetry = (missionId: string, scenarioId: string, groupId: string, satelliteId: string, satelliteName: string, scenarioName: string) => {
        return new Promise<{ status: boolean; message: string }>(async (resolve, reject) => {
            try {
                const satelliteName = "Satellite";

                // ---- API CALL ----
                const response = await SimulateService.exportThermalDataFile(
                    missionId,
                    scenarioId,
                    groupId,
                    satelliteId
                );

                if (!response?.data) {
                    reject({ status: false, message: "No data received from server" });
                    return;
                }

                // ---- PREPARE FILE ----
                const blob = new Blob([response.data], {
                    type: "text/csv;charset=utf-8;",
                });

                const url = URL.createObjectURL(blob);

                const safeScenario = (scenarioName || "Scenario").replace(/[^a-zA-Z0-9_-]/g, "_");
                const safeSatName = satelliteName.replace(/[^a-zA-Z0-9_-]/g, "_");

                const filename = `${safeScenario}_${safeSatName}.csv`;

                // ---- DOWNLOAD ----
                const downloadPromise = new Promise<void>((res) => {
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = filename;

                    document.body.appendChild(link);
                    link.click();

                    setTimeout(() => {
                        document.body.removeChild(link);
                        res();
                    }, 50);
                });

                await downloadPromise;

                URL.revokeObjectURL(url);

                resolve({
                    status: true,
                    message: "Telemetry exported successfully",
                });

            } catch (error) {
                console.error("EXPORT ERROR:", error);
                reject({
                    status: false,
                    message: "Failed to export telemetry",
                });
            }
        });
    };

    const uploadThermalZip = async (
        file: File,
        missionId: string,
        scenarioId: string,
        scenario: ScenarioResponseType,
        previousPresimData: PresimDataType,
        selectedSatellite: string
    ): Promise<{ status: "success" | "error" | "info"; message: string }> => {

        const isZip =
            file.type === 'application/zip' ||
            file.name.toLowerCase().endsWith('.zip');

        if (!isZip) {
            return { status: "info", message: "Only .zip files are supported" };
        }

        try {
            const file_data = new FormData();
            file_data.append("file", file);

            await SimulateService.importThermalTelemetry(
                missionId,
                selectedSatellite,
                scenarioId,
                file_data
            );

            const response = await getPresimData(
                missionId,
                scenarioId,
                scenario,
                previousPresimData,
                selectedSatellite
            );

            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev.assets,
                    presim: {
                        ...prev.assets.presim,
                        data: {
                            ...prev.assets.presim.data,
                            ...response
                        }
                    },
                }
            }));

            return { status: "success", message: "Import successful" };

        } catch {
            return { status: "error", message: "Failed to import" };
        }
    };

    const handleImportThermalTelemetry = (
        missionId: string,
        scenarioId: string,
        scenario: ScenarioResponseType,
        previousPresimData: PresimDataType,
        selectedSatellite: string
    ) => {
        return new Promise((resolve, reject) => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.zip';

            fileInput.onchange = async (event) => {
                const input = event.target as HTMLInputElement;
                if (!input?.files?.[0]) {
                    reject({ status: "error", message: "No file selected" });
                    return;
                }

                const result = await uploadThermalZip(
                    input.files[0],
                    missionId,
                    scenarioId,
                    scenario,
                    previousPresimData,
                    selectedSatellite
                );

                result.status === "success" ? resolve(result) : reject(result);
            };

            fileInput.click();
        });
    };



    return {
        postPresimData,
        setPresimLoader,
        getPresimData,
        stopAllPresimLoaders,
        handleExportTelemetry,
        uploadThermalZip,
        handleImportThermalTelemetry,
    }
}

export default usePresimApis